import { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { AuthContext } from '@/context/auth.context';
import { useViewMode } from '@/context/view-mode.context';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import MDButton from 'src/components/md/MDButton';
import {
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { LocationPicker } from './LocationPicker';
import { CustomersAutocomplete } from '@/components/customer/CustomersAutocomplete';
import CustomerPill from 'src/components/customer/CustomerPill';
import LocationPill from 'src/components/location/LocationPill';
import Icon from '@mui/material/Icon';
import useSalesOrderDraft from 'src/api/accounts-receivable/sales-order-drafts/useSalesOrderDraft';
import useValidateSalesOrderDraftInventory from 'src/api/accounts-receivable/sales-order-drafts/useValidateSalesOrderDraftInventory';
import useValidateSalesOrderDraftDiscounts from 'src/api/accounts-receivable/sales-order-drafts/useValidateSalesOrderDraftDiscounts';
import useUpdateSalesOrderDraftField from 'src/api/accounts-receivable/sales-order-drafts/useUpdateSalesOrderDraftField';
import useUpdateSalesOrderDraftLineItem from 'src/api/accounts-receivable/sales-order-drafts/useUpdateSalesOrderDraftLineItem';
import useCreateSalesOrderDraftLineItem from 'src/api/accounts-receivable/sales-order-drafts/useCreateSalesOrderDraftLineItem';
import useDeleteSalesOrderDraftLineItem from 'src/api/accounts-receivable/sales-order-drafts/useDeleteSalesOrderDraftLineItem';
import useApproveSalesOrderDraft from 'src/api/accounts-receivable/sales-order-drafts/useApproveSalesOrderDraft';
import useRejectSalesOrderDraft from 'src/api/accounts-receivable/sales-order-drafts/useRejectSalesOrderDraft';
import useUpdatePurchaseOrderCustomer from 'src/api/accounts-receivable/useUpdatePurchaseOrderCustomer';
import useMatchLineItemToProductVariant from 'src/api/inventory-management/products/useMatchLineItemToProductVariant';
import {
  useAddSalesOrderDraftCharge,
  useUpdateSalesOrderDraftCharge,
  useDeleteSalesOrderDraftCharge,
} from 'src/api/accounts-receivable/sales-order-drafts/useManageSalesOrderDraftCharges';
import AssociateLineItemModal from './AssociateLineItemModal';
import { EnhancedLineItemsTable } from './line-item-table/EnhancedLineItemsTable';
import DeleteLineItemModal from './DeleteLineItemModal';
import RejectSalesOrderDraftModal from './RejectSalesOrderDraftModal';
import ChargesSection from './ChargesSection';
import SourceBadge from 'src/components/source-badge/SourceBadge';
import {
  SalesOrderSource,
  SalesOrderDraftStatus,
  LineItem,
  Charge,
} from 'src/types/application/SalesOrderDraftDTO';
import EdiTransactionViewer from './sales-order-draft-modal/EdiTransactionViewer';
import { SmartEmailViewer } from '@/components/email-viewer/SmartEmailViewer';
import { EmailData } from '@/components/email-viewer/email-viewer-types';
import { useToast } from '@/context/ToastContext';
import { useFeatureFlag } from '@/integrations/feature-flags/feature-flags';
import type { Column } from 'react-table';
import { DatePicker } from '@/components/common/date-pickers/date-picker';
import { cn } from '@/lib/utils';
import { MemoryBankNotes } from '@/components/memory-bank/MemoryBankNotes';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { QuestionIcon } from '@phosphor-icons/react';
import { SystemEntity } from '@erp/shared-types';
import { ReextractSalesOrderDraftDialog } from '@/components/purchase-orders/ReextractSalesOrderDraftDialog';
import { RobotRetryButton } from '@/components/common/robot-retry-button';
import { useQueryClient } from '@tanstack/react-query';
import { CustomerProductDTO } from 'src/types/application/inventory-management/CustomerProductDTO';
import { ProductVariantDTO } from 'src/types/application/inventory-management/ProductVariantDTO';
import { inboundEmailToEmailData } from '@/components/email-viewer/utils';
import { SalesOrderSyncStatus } from '@/types/application/SalesOrderDTOV2_DEPRECATED';
import OptionsMenu from './sales-order-draft-modal/OptionsMenu';
import config from '@/config';
import { hasAddressData } from '@/utils/address-utils';
import {
  getSourceFlags,
  extractEmailDomain,
  formatDateOnly,
  parseDateOnly,
  isDatePast,
} from './sales-order-draft-modal/utils';
import type { ValidationError } from './sales-order-draft-modal/types';
import { SyncErrorDialog } from './sales-order-draft-modal/SyncErrorDialog';
import { SourceDocumentViewer } from './sales-order-draft-modal/SourceDocumentViewer';
import { ActivityGraph } from '@/components/activity-graph';
import {
  AiSuggestionBanner,
  AiSuggestionLoading,
  AiSuggestionError,
  SuggestedActionsPanel,
} from '@/components/ai-suggestions';
import type { SuggestedActionWithStatus } from '@erp/shared-types';
import { EmailDraftEditor } from '@/components/email-draft-editor';
import {
  useAiSuggestionByEntity,
  useAcceptAiSuggestion,
  useDismissAiSuggestion,
  useGenerateAiSuggestion,
  useExecuteSuggestedAction,
} from '@/api/ai';
import { useEmailDraft } from '@/api/communications/useEmailDraft';
import { useUpdateEmailDraft } from '@/api/communications/useUpdateEmailDraft';
import { useSendEmailDraft } from '@/api/communications/useSendEmailDraft';

const showChargeModal = false;
const showReextractButton = false;

interface Props {
  salesOrderDraftId: string;
  editable: boolean;
  onUpdate?: () => void;
  onApproved?: (salesOrderId: string) => void;
  onRejected?: () => void;
  onReadyForApprovalChange?: (isReady: boolean) => void;
  onApproveOrder?: () => void;
  showInventoryColumn?: boolean;
}

/**
 * Reusable view component for Sales Order Draft content.
 * This component can be used in both the modal and full-screen page views.
 */
export default function SalesOrderDraftView({
  salesOrderDraftId,
  editable,
  onUpdate,
  onApproved,
  onRejected,
  onReadyForApprovalChange,
  onApproveOrder,
  showInventoryColumn,
}: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentUser } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const { enabled: isMemoryBankNotesEnabled } = useFeatureFlag(
    'memory-bank-notes-enabled',
    false,
  );

  const { enabled: isActivityLogEnabled } = useFeatureFlag(
    'activity-log-enabled',
    false,
  );

  const {
    data: salesOrderDraftData,
    isLoading: isSalesOrderDraftLoading,
    refetch: refetchSalesOrderDraft,
  } = useSalesOrderDraft(salesOrderDraftId);

  const salesOrderDraft = salesOrderDraftData?.stagedPurchaseOrder;

  // Fetch inventory validation data when showInventoryColumn is enabled
  const { data: inventoryData, isPendingOrLoading: isInventoryLoading } =
    useValidateSalesOrderDraftInventory({
      salesOrderDraftId,
      enabled: !!salesOrderDraft && !!showInventoryColumn,
    });

  // Always fetch discount validation data (no feature flag check)
  const { data: discountData, isPendingOrLoading: isDiscountLoading } =
    useValidateSalesOrderDraftDiscounts({
      salesOrderDraftId,
      enabled: !!salesOrderDraft,
    });

  // AI Suggestion state and hooks
  const {
    suggestion: aiSuggestion,
    isLoading: isAiSuggestionLoading,
    isGenerating: isAiSuggestionGenerating,
    isFailed: isAiSuggestionFailed,
  } = useAiSuggestionByEntity(
    'staged_customer_purchase_order',
    salesOrderDraftId,
    !!salesOrderDraft,
  );

  const emailDraftId = aiSuggestion?.emailDraftId ?? undefined;
  const { draft: emailDraft, isLoading: isEmailDraftLoading } = useEmailDraft(
    emailDraftId,
    !!emailDraftId,
  );

  const [isEmailDraftEditorOpen, setIsEmailDraftEditorOpen] = useState(false);

  const acceptSuggestion = useAcceptAiSuggestion((data) => {
    // If draft was deleted (approve_order confirmation), navigate back to list
    if (data.draftDeleted || data.orderApproved) {
      onRejected?.();
    } else {
      refetchSalesOrderDraft();
    }
  });

  const dismissSuggestion = useDismissAiSuggestion(() => {
    refetchSalesOrderDraft();
  });

  // Compute if order is ready for approval based on AI suggestion state.
  // Ready when: (a) only action left is approve_order, or
  // (b) all actions have been applied (empty array).
  const isReadyForApproval =
    aiSuggestion?.suggestedActions != null &&
    ((aiSuggestion.suggestedActions.length === 1 &&
      aiSuggestion.suggestedActions[0]?.type === 'approve_order') ||
      aiSuggestion.suggestedActions.length === 0);

  // Notify parent of ready state changes
  useEffect(() => {
    onReadyForApprovalChange?.(!!isReadyForApproval);
  }, [isReadyForApproval, onReadyForApprovalChange]);

  const updateEmailDraft = useUpdateEmailDraft(() => {});

  const sendEmailDraft = useSendEmailDraft(() => {
    setIsEmailDraftEditorOpen(false);
    refetchSalesOrderDraft();
  });

  const generateSuggestion = useGenerateAiSuggestion(() => {
    refetchSalesOrderDraft();
  });

  const executeSuggestedAction = useExecuteSuggestedAction(() => {
    refetchSalesOrderDraft();
  });
  const [applyingActionIndex, setApplyingActionIndex] = useState<number | null>(
    null,
  );

  const customerId =
    salesOrderDraft?.customerId || salesOrderDraft?.suggestedCustomerId;

  const customerName =
    salesOrderDraft?.customer?.erpDisplayName ||
    salesOrderDraft?.customer?.name;

  const sourceFlags = getSourceFlags(salesOrderDraft?.source);
  const { isEdiSource, isEmailSource, isDirectEntry, isFileUpload } =
    sourceFlags;

  // Check if sales order draft is already processed (approved or rejected)
  const isProcessed =
    salesOrderDraft?.status === SalesOrderDraftStatus.APPROVED ||
    salesOrderDraft?.status === SalesOrderDraftStatus.REJECTED;

  // View should only be editable if editable prop is true AND the PO isn't already processed
  const isViewEditable = editable && !isProcessed;

  // Check if we can show the "Get AI Help" button
  const canGenerateSuggestion =
    isEmailSource &&
    !isProcessed &&
    !salesOrderDraft?.currentAiSuggestionId &&
    !generateSuggestion.isPending;

  const customerEmailDomain = useMemo(
    () =>
      salesOrderDraft?.customer?.emailDomain ||
      salesOrderDraft?.suggestedCustomer?.emailDomain ||
      extractEmailDomain(salesOrderDraft?.sourceEmail?.from),
    [
      salesOrderDraft?.customer?.emailDomain,
      salesOrderDraft?.suggestedCustomer?.emailDomain,
      salesOrderDraft?.sourceEmail?.from,
    ],
  );

  // State for right panel visibility
  const [isRightPanelVisible, setIsRightPanelVisible] = useState<boolean>(true);

  // State for error dialog
  const [syncErrorDialogOpen, setSyncErrorDialogOpen] = useState(false);
  const [syncErrorMessage, setSyncErrorMessage] = useState<string | null>(null);
  const [erpSystemName, setErpSystemName] = useState<string | null>(null);

  const updateStagedPurchaseOrderLineItem = useUpdateSalesOrderDraftLineItem(
    salesOrderDraftId,
    () => {
      setOptimisticUpdates({});
      setPendingUpdates({});
      onUpdate?.();
      refetchSalesOrderDraft();
    },
  );

  const createLineItem = useCreateSalesOrderDraftLineItem(
    salesOrderDraftId,
    () => {
      onUpdate?.();
      refetchSalesOrderDraft();
    },
  );

  const deleteLineItem = useDeleteSalesOrderDraftLineItem(
    salesOrderDraftId,
    () => {
      onUpdate?.();
      refetchSalesOrderDraft();
    },
  );

  const addOrderCharge = useAddSalesOrderDraftCharge(salesOrderDraftId, () => {
    onUpdate?.();
    refetchSalesOrderDraft();
  });

  const updateOrderCharge = useUpdateSalesOrderDraftCharge(
    salesOrderDraftId,
    () => {
      onUpdate?.();
      refetchSalesOrderDraft();
    },
  );

  const deleteOrderCharge = useDeleteSalesOrderDraftCharge(
    salesOrderDraftId,
    () => {
      onUpdate?.();
      refetchSalesOrderDraft();
    },
  );

  const matchLineItemToProductVariant = useMatchLineItemToProductVariant(() => {
    onUpdate?.();
    refetchSalesOrderDraft();
  });

  const updatePurchaseOrderField = useUpdateSalesOrderDraftField(
    salesOrderDraftId,
    () => {
      onUpdate?.();
      refetchSalesOrderDraft();
    },
  );

  const updateCustomer = useUpdatePurchaseOrderCustomer(
    salesOrderDraftId,
    () => {
      onUpdate?.();
      refetchSalesOrderDraft();
      queryClient.invalidateQueries({
        queryKey: [`core/watches/staged_purchase_order/${salesOrderDraftId}`],
      });
    },
  );

  const handleCustomerSelect = useCallback(
    (customerId: string | undefined) => {
      if (customerId) {
        updateCustomer.mutateAsync({ customerId });
      }
    },
    [updateCustomer],
  );

  const emailData: EmailData = salesOrderDraft?.sourceData?.email
    ? {
        ...inboundEmailToEmailData(salesOrderDraft.sourceData.email),
        documentAssociations:
          salesOrderDraft?.sourceEmail?.documentAssociations,
      }
    : {
        inboundEmailId: undefined,
        subject: undefined,
        from: undefined,
        to: undefined,
        htmlContent: undefined,
        textContent: undefined,
        attachments: undefined,
        documentAssociations:
          salesOrderDraft?.sourceEmail?.documentAssociations,
      };

  const ediData = {
    ordefulMessage: salesOrderDraft?.sourceData?.orderfulJson,
  };

  const documentData = {
    document: salesOrderDraft?.sourceData?.document,
  };

  const [selectedLineItemIndex, setSelectedLineItemIndex] = useState<number>();
  const [selectedLineItemSnapshot, setSelectedLineItemSnapshot] =
    useState<LineItem>();
  const [showDeliveryDateModal, setShowDeliveryDateModal] = useState(false);
  const [showShipDateModal, setShowShipDateModal] = useState(false);
  const [isEditingPurchaseOrderNumber, setIsEditingPurchaseOrderNumber] =
    useState(false);
  const [purchaseOrderNumberInput, setPurchaseOrderNumberInput] = useState('');
  const [showDeleteLineItemModal, setShowDeleteLineItemModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showReextractDialog, setShowReextractDialog] = useState(false);
  const [deletingLineItem, setDeletingLineItem] = useState<
    LineItem | undefined
  >();
  const [deletingLineItemIndex, setDeletingLineItemIndex] = useState<
    number | undefined
  >();
  const { showToast } = useToast();

  const [validationErrors, setValidationErrors] = useState<ValidationError>({});

  const [optimisticUpdates, setOptimisticUpdates] = useState<
    Record<number, Partial<LineItem>>
  >({});

  const [pendingUpdates, setPendingUpdates] = useState<
    Record<
      number,
      Set<
        | 'quantity'
        | 'unitPrice'
        | 'rescheduledDate'
        | 'customerDescription'
        | 'customerIdentifier'
      >
    >
  >({});

  const lineItems: LineItem[] = useMemo(
    () => salesOrderDraft?.lineItems || [],
    [salesOrderDraft?.lineItems],
  );

  const displayLineItems = useMemo(
    () =>
      lineItems.map((item, index) => ({
        ...item,
        ...optimisticUpdates[index],
      })),
    [lineItems, optimisticUpdates],
  );

  const handleAssociateVariantSelected = useCallback(
    (rowIndex: number, item: LineItem, productVariantId?: string) => {
      if (!productVariantId || !customerId) return;
      matchLineItemToProductVariant.mutate({
        stagedCustomerPurchaseOrderId: salesOrderDraftId,
        stagedCustomerPurchaseOrderLineItemIndex: rowIndex,
        customerId,
        productVariantId,
        customerDescription:
          item.customerDescription || item.itemName || item.name,
        customerMainIdentifier: item.customerIdentifier || item.sku || item.upc,
      });
    },
    [customerId, matchLineItemToProductVariant, salesOrderDraftId],
  );

  const selectedLineItem = selectedLineItemSnapshot;

  const handleUpdateLineItem = (
    index: number,
    field:
      | 'quantity'
      | 'unitPrice'
      | 'rescheduledDate'
      | 'customerDescription'
      | 'customerIdentifier',
    value: number | string,
  ) => {
    const lineItem = lineItems[index];
    if (!lineItem) return;

    setOptimisticUpdates((prev) => ({
      ...prev,
      [index]: {
        ...prev[index],
        [field]: value,
      },
    }));

    setPendingUpdates((prev) => ({
      ...prev,
      [index]: new Set([...(prev[index] || []), field]),
    }));

    const updatedLineItem = {
      ...lineItem,
      [field]: value,
    };

    updateStagedPurchaseOrderLineItem.mutate(
      {
        index,
        before: lineItem,
        after: updatedLineItem,
      },
      {
        onError: () => {
          setOptimisticUpdates((prev) => {
            const newUpdates = { ...prev };
            if (newUpdates[index]) {
              delete newUpdates[index][field as keyof LineItem];
              if (Object.keys(newUpdates[index]).length === 0) {
                delete newUpdates[index];
              }
            }
            return newUpdates;
          });

          setPendingUpdates((prev) => {
            const newPending = { ...prev };
            if (newPending[index]) {
              newPending[index].delete(field);
              if (newPending[index].size === 0) {
                delete newPending[index];
              }
            }
            return newPending;
          });
        },
      },
    );
  };

  const handleAddLineItem = () => {
    createLineItem.mutate({
      customerIdentifier: '',
      customerDescription: '',
      quantity: 1,
      unitPrice: 0,
    });
  };

  const handleDeleteLineItem = (index: number) => {
    const lineItem = lineItems[index];
    setDeletingLineItem(lineItem);
    setDeletingLineItemIndex(index);
    setShowDeleteLineItemModal(true);
  };

  const handleToggleRejectLineItem = (index: number) => {
    const lineItem = lineItems[index];
    if (!lineItem) return;

    const updatedLineItem = {
      ...lineItem,
      ediRejected: !lineItem.ediRejected,
    };

    updateStagedPurchaseOrderLineItem.mutate({
      index,
      before: lineItem,
      after: updatedLineItem,
    });
  };

  const handleConfirmDelete = () => {
    if (deletingLineItemIndex !== undefined) {
      deleteLineItem.mutate({ index: deletingLineItemIndex });
      setShowDeleteLineItemModal(false);
      setDeletingLineItem(undefined);
      setDeletingLineItemIndex(undefined);
    }
  };

  const { enabled: isAssociateProductV2Enabled } = useFeatureFlag(
    'associate-product-v2-enabled',
    false,
  );

  const childLineItemColumns: Column<LineItem>[] = useMemo(
    () => [
      {
        Header: 'ID',
        accessor: 'customerIdentifier',
        Cell: ({ row }: { row: { original: LineItem } }) => {
          const item = row.original as LineItem;
          const identifier = item.customerIdentifier || item.sku || item.upc;
          return identifier ? (
            <MDTypography variant="caption">{identifier}</MDTypography>
          ) : (
            <MDTypography variant="caption" color="text.secondary">
              -
            </MDTypography>
          );
        },
      },
      {
        Header: 'Item',
        accessor: 'customerDescription',
        Cell: ({ row }: { row: { original: LineItem } }) => {
          const item = row.original as LineItem;
          const description =
            item.customerDescription ||
            item.itemName ||
            item.name ||
            'No description available';
          return (
            <MDBox display="flex" flexDirection="column">
              <MDTypography variant="caption" color="text.secondary">
                {description}
              </MDTypography>
            </MDBox>
          );
        },
      },
      {
        Header: 'Unit Price',
        accessor: 'unitPrice',
        Cell: ({ value }: { value?: number | null }) => {
          return <>{value ? `$${Number(value).toFixed(2)}` : ''}</>;
        },
      },
    ],
    [],
  );

  const shouldShowAssociateModal =
    selectedLineItemIndex !== undefined && !!selectedLineItem && !!customerId;

  const handleVariantSelected = useCallback(
    ({ productVariantId }: { productVariantId?: string }) => {
      if (productVariantId) {
        if (
          selectedLineItemIndex !== undefined &&
          customerId &&
          selectedLineItem
        ) {
          matchLineItemToProductVariant.mutate({
            stagedCustomerPurchaseOrderId: salesOrderDraftId,
            stagedCustomerPurchaseOrderLineItemIndex: selectedLineItemIndex,
            customerId,
            productVariantId,
            customerDescription:
              selectedLineItem.customerDescription ||
              selectedLineItem.itemName ||
              selectedLineItem.name,
            customerMainIdentifier:
              selectedLineItem.customerIdentifier ||
              selectedLineItem.sku ||
              selectedLineItem.upc,
          });
        } else {
          if (!customerId) {
            window.alert(
              'Customer ID is required to associate a line item to a product variant.',
            );
          } else {
            window.alert(
              'Something went wrong. Please contact the Gently team.',
            );
          }
        }
      }
      setSelectedLineItemIndex(undefined);
      setSelectedLineItemSnapshot(undefined);
    },
    [
      selectedLineItemIndex,
      customerId,
      selectedLineItem,
      matchLineItemToProductVariant,
      salesOrderDraftId,
    ],
  );

  const renderAssociateModal = useCallback(() => {
    if (!shouldShowAssociateModal) return null;

    if (isAssociateProductV2Enabled) {
      return null;
    }

    return (
      <AssociateLineItemModal
        lineItem={selectedLineItem}
        customerId={customerId}
        editable={isViewEditable}
        onClose={(productVariantId) => {
          handleVariantSelected({ productVariantId });
        }}
      />
    );
  }, [
    shouldShowAssociateModal,
    isAssociateProductV2Enabled,
    customerId,
    selectedLineItem,
    isViewEditable,
    handleVariantSelected,
  ]);

  const handleAddOrderCharge = (charge: Charge) => {
    addOrderCharge.mutate({ charge });
  };

  const handleUpdateOrderCharge = (charge: Charge) => {
    if (charge.id) {
      updateOrderCharge.mutate({ charge, chargeId: charge.id });
    }
  };

  const handleDeleteOrderCharge = (chargeId: string) => {
    deleteOrderCharge.mutate({ chargeId });
  };

  const handleInlineShipDateUpdate = (next: Date | undefined) => {
    if (!next) return;
    const parsed = parseDateOnly(next);
    if (!parsed) return;
    const nextDateOnly = parsed;
    const delivery = parseDateOnly(salesOrderDraft?.deliveryDate);
    if (delivery && nextDateOnly > delivery) {
      showToast({
        title: 'Invalid date',
        message: 'Ship date must be on or before the delivery date.',
        color: 'error',
        icon: 'warning',
      });
      return;
    }
    updateField.mutate({
      field: 'shipDate',
      value: formatDateOnly(nextDateOnly),
    });
  };

  const handleInlineDeliveryDateUpdate = (next: Date | undefined) => {
    if (!next) return;
    const parsed = parseDateOnly(next);
    if (!parsed) return;
    const nextDate = parsed;
    const ship = parseDateOnly(salesOrderDraft?.shipDate);
    if (ship && nextDate < ship) {
      showToast({
        title: 'Invalid date',
        message: 'Delivery date must be on or after the ship date.',
        color: 'error',
        icon: 'warning',
      });
      return;
    }
    updateField.mutate({
      field: 'deliveryDate',
      value: formatDateOnly(nextDate),
    });
  };

  const updateField = useUpdateSalesOrderDraftField(salesOrderDraftId, () => {
    onUpdate?.();
    refetchSalesOrderDraft();
    setShowDeliveryDateModal(false);
    setShowShipDateModal(false);
    setIsEditingPurchaseOrderNumber(false);
  });

  const isLoading =
    isSalesOrderDraftLoading ||
    updateStagedPurchaseOrderLineItem.isLoading ||
    createLineItem.isLoading ||
    deleteLineItem.isLoading ||
    matchLineItemToProductVariant.isLoading;

  useEffect(() => {
    setIsRightPanelVisible(true);
  }, [isEdiSource]);

  useEffect(() => {
    setOptimisticUpdates({});
  }, [salesOrderDraft?.lineItems]);

  useEffect(() => {
    if (isEditingPurchaseOrderNumber) {
      setPurchaseOrderNumberInput(salesOrderDraft?.purchaseOrderNumber || '');
    }
  }, [isEditingPurchaseOrderNumber, salesOrderDraft?.purchaseOrderNumber]);

  const handleToggleRightPanel = () => {
    setIsRightPanelVisible(!isRightPanelVisible);
  };

  const validateForm = () => {
    const errors: ValidationError = {};

    if (!salesOrderDraft?.shipToLocationId) {
      errors.shippingLocation = 'Shipping location is required';
    }

    const inactiveProducts = salesOrderDraft?.lineItems?.filter((lineItem) => {
      const customerProductId = lineItem.selectedCustomerProductId;
      const variantId = lineItem.selectedProductVariantId;

      const customerProductData = queryClient.getQueryData<{
        customerProduct: CustomerProductDTO;
      }>([
        `inventory-management/products-v2/customer-products/${customerProductId}`,
        undefined,
      ]);

      if (customerProductData) {
        return (
          customerProductData.customerProduct.productVariant.active === false
        );
      }

      const variantData = queryClient.getQueryData<{
        productVariant: ProductVariantDTO;
      }>([
        `inventory-management/products-v2/product-variants/${variantId}`,
        undefined,
      ]);

      return variantData?.productVariant?.active === false;
    });

    if (inactiveProducts?.length) {
      errors.inactiveProducts = `All selected products must be active. Inactive products: ${inactiveProducts
        .map((item) => {
          return item.customerDescription || item.itemName || item.name;
        })
        .join(', ')}`;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleLocationSelect = (locationId?: string) => {
    if (locationId) {
      updatePurchaseOrderField.mutate({
        field: 'shipToLocationId',
        value: locationId,
      });
      setValidationErrors((prev) => ({ ...prev, shippingLocation: undefined }));
    }
  };

  const handleBillToLocationSelect = (locationId?: string) => {
    if (locationId) {
      updatePurchaseOrderField.mutate({
        field: 'billToLocationId',
        value: locationId,
      });
      setValidationErrors((prev) => ({ ...prev, billToLocation: undefined }));
    }
  };

  const handleBuyingPartyLocationSelect = (locationId?: string) => {
    if (locationId) {
      updatePurchaseOrderField.mutate({
        field: 'buyingPartyLocationId',
        value: locationId,
      });
      setValidationErrors((prev) => ({
        ...prev,
        buyingPartyLocation: undefined,
      }));
    }
  };

  // AI Suggestion handlers
  const handleReviewEmailDraft = () => {
    if (aiSuggestion?.emailDraftId) {
      setIsEmailDraftEditorOpen(true);
    }
  };

  const handleAcceptSuggestion = () => {
    if (aiSuggestion?.id) {
      // Pass deleteDraft: true for approve_order type to enable lenient mode
      // This ensures the Confirm button never throws errors
      acceptSuggestion.mutate({
        id: aiSuggestion.id,
        deleteDraft: aiSuggestion.suggestionType === 'approve_order',
      });
    }
  };

  const handleDismissSuggestion = () => {
    if (aiSuggestion?.id) {
      dismissSuggestion.mutate({ id: aiSuggestion.id });
    }
  };

  const handleRefreshSuggestion = () => {
    generateSuggestion.mutate({
      entityType: 'staged_customer_purchase_order',
      entityId: salesOrderDraftId,
      force: true,
    });
  };

  // Multi-action handlers
  const handleApplySuggestedAction = async (
    action: SuggestedActionWithStatus,
  ) => {
    if (action.type === 'approve_order') {
      onApproveOrder?.();
      return;
    }
    if (action.type === 'send_email_draft') {
      handleReviewEmailDraft();
      return;
    }

    if (!aiSuggestion?.id) {
      console.error('Cannot apply action: no suggestion ID available');
      return;
    }

    setApplyingActionIndex(action.index);
    try {
      await executeSuggestedAction.mutateAsync({
        suggestionId: aiSuggestion.id,
        actionIndex: action.index,
        stagedOrderId: salesOrderDraftId,
        actionType: action.type,
        lineItemIndex: action.lineItemIndex,
        lineItemIdentifier: action.lineItemIdentifier,
        newPrice: action.newPrice,
        deliveryDate: action.deliveryDate,
        shipDate: action.shipDate,
        locationId: action.locationId,
      });
    } finally {
      setApplyingActionIndex(null);
    }
  };

  const handleDismissSuggestedAction = (_action: SuggestedActionWithStatus) => {
    // Just dismiss locally
  };

  const handleReviewSuggestedDraft = (action: SuggestedActionWithStatus) => {
    if (action.type === 'send_email_draft' && aiSuggestion?.emailDraftId) {
      setIsEmailDraftEditorOpen(true);
    }
  };

  const handleSaveEmailDraft = async (updates: {
    subject?: string;
    htmlContent?: string;
    to?: string[];
    cc?: string[];
  }) => {
    if (emailDraft?.id) {
      await updateEmailDraft.mutateAsync({ id: emailDraft.id, ...updates });
    }
  };

  const handleSendEmailDraft = async () => {
    if (emailDraft?.id) {
      await sendEmailDraft.mutateAsync({ id: emailDraft.id });
    }
  };

  const handleDiscardEmailDraft = () => {
    setIsEmailDraftEditorOpen(false);
  };

  if (!salesOrderDraft) {
    return null;
  }

  return (
    <MDBox sx={{ height: '100%', overflow: 'hidden' }}>
      <Grid container spacing={2} sx={{ height: '100%', overflow: 'hidden' }}>
        {/* Left side - PO Info & Line Items */}
        <Grid
          item
          xs={12}
          md={isRightPanelVisible ? (isActivityLogEnabled ? 7 : 6) : 12}
          sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        >
          <MDBox mb={2} sx={{ flexShrink: 0 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <MDBox
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '140px 1fr',
                    gap: 1,
                    '& .label': {
                      fontWeight: 'bold',
                      fontSize: 14,
                      display: 'flex',
                      alignItems: 'center',
                    },
                  }}
                >
                  <div className="flex items-center gap-1">
                    <MDTypography className="label">
                      Customer PO #:
                    </MDTypography>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <QuestionIcon className="h-4 w-4 text-gray-500 hover:text-gray-700 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p>
                            The purchase order number provided by the customer.
                            This helps match incoming orders to customer records
                            and invoices.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <div className="flex items-center justify-between">
                    {isViewEditable ? (
                      isEditingPurchaseOrderNumber ? (
                        <TextField
                          size="small"
                          value={purchaseOrderNumberInput}
                          onChange={(e) =>
                            setPurchaseOrderNumberInput(e.target.value)
                          }
                          onBlur={() => {
                            const trimmed = purchaseOrderNumberInput.trim();
                            if (
                              trimmed &&
                              trimmed !== salesOrderDraft?.purchaseOrderNumber
                            ) {
                              updateField.mutate({
                                field: 'purchaseOrderNumber',
                                value: trimmed,
                              });
                            }
                            setIsEditingPurchaseOrderNumber(false);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              const trimmed = purchaseOrderNumberInput.trim();
                              if (trimmed) {
                                updateField.mutate({
                                  field: 'purchaseOrderNumber',
                                  value: trimmed,
                                });
                              }
                              setIsEditingPurchaseOrderNumber(false);
                            } else if (e.key === 'Escape') {
                              setIsEditingPurchaseOrderNumber(false);
                            }
                          }}
                          autoFocus
                          sx={{
                            minWidth: '120px',
                            '& .MuiInputBase-input': {
                              fontSize: '0.875rem',
                              padding: '4px 8px',
                            },
                          }}
                        />
                      ) : salesOrderDraft.purchaseOrderNumber ? (
                        <MDTypography
                          variant="body2"
                          sx={{
                            cursor: 'pointer',
                          }}
                          onClick={() => setIsEditingPurchaseOrderNumber(true)}
                        >
                          {salesOrderDraft.purchaseOrderNumber}
                        </MDTypography>
                      ) : (
                        <MDBox
                          onClick={() => setIsEditingPurchaseOrderNumber(true)}
                          sx={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <Icon color="error" sx={{ fontSize: 20 }}>
                            warning
                          </Icon>
                          <MDTypography variant="caption" color="error">
                            Missing PO Number
                          </MDTypography>
                        </MDBox>
                      )
                    ) : salesOrderDraft.purchaseOrderNumber ? (
                      <MDTypography variant="body2">
                        {salesOrderDraft.purchaseOrderNumber}
                      </MDTypography>
                    ) : (
                      <MDBox
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        <Icon color="error" sx={{ fontSize: 20 }}>
                          warning
                        </Icon>
                        <MDTypography variant="caption" color="error">
                          Missing PO Number
                        </MDTypography>
                      </MDBox>
                    )}
                  </div>

                  <MDTypography className="label">Source:</MDTypography>
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <SourceBadge
                      source={salesOrderDraft.source}
                      onClick={handleToggleRightPanel}
                    />
                    {showReextractButton &&
                      isEmailSource &&
                      emailData.inboundEmailId && (
                        <RobotRetryButton
                          onClick={() => setShowReextractDialog(true)}
                          tooltip="Re-extract Sales Order Draft"
                        >
                          Re-extract
                        </RobotRetryButton>
                      )}
                  </MDBox>

                  <MDBox className="label">Customer:</MDBox>
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <MDBox sx={{ width: '100%', maxWidth: 400 }}>
                      {isViewEditable ? (
                        <CustomersAutocomplete
                          selectedCustomerId={salesOrderDraft.customer?.id}
                          onCustomerSelect={handleCustomerSelect}
                          allowClear={false}
                          className="w-full"
                        />
                      ) : salesOrderDraft.customer ? (
                        <CustomerPill
                          customer={{
                            name:
                              salesOrderDraft.customer.erpDisplayName ??
                              salesOrderDraft.customer.name,
                            emailDomain: salesOrderDraft.customer.emailDomain,
                            phoneNumber: salesOrderDraft.customer.phoneNumber,
                          }}
                        />
                      ) : (
                        <MDTypography variant="body2" color="text.secondary">
                          No customer assigned
                        </MDTypography>
                      )}
                    </MDBox>
                    {isMemoryBankNotesEnabled && customerId && (
                      <MemoryBankNotes
                        staticMetadata={{
                          workflowStep: 'EXTRACT_SALES_ORDER',
                        }}
                        dynamicMetadata={{
                          customerEmailDomain,
                        }}
                        entityId={customerId}
                        entityType={SystemEntity.AR_CUSTOMER}
                        onNotesChange={refetchSalesOrderDraft}
                        tooltipText="Add notes to guide the AI when processing orders from this customer"
                      />
                    )}
                  </MDBox>

                  <MDBox className="label">Ship To Location:</MDBox>
                  <MDBox sx={{ width: '100%', maxWidth: 400 }}>
                    {customerId ? (
                      isViewEditable ? (
                        <LocationPicker
                          customerId={customerId}
                          selectedLocationId={salesOrderDraft.shipToLocationId}
                          onLocationSelect={handleLocationSelect}
                          error={validationErrors.shippingLocation}
                          siteUseCode="SHIP_TO"
                        />
                      ) : salesOrderDraft.shipToLocationId ? (
                        <LocationPill
                          locationId={salesOrderDraft.shipToLocationId}
                          customerId={customerId}
                        />
                      ) : (
                        <MDTypography variant="body2" color="text.secondary">
                          No location assigned
                        </MDTypography>
                      )
                    ) : (
                      <MDTypography variant="body2">
                        Please select a customer
                      </MDTypography>
                    )}
                  </MDBox>

                  {isEdiSource &&
                  hasAddressData(salesOrderDraft.billToAddress) ? (
                    <>
                      <MDBox className="label">Bill To Location:</MDBox>
                      <MDBox sx={{ width: '100%', maxWidth: 400 }}>
                        {customerId ? (
                          isViewEditable ? (
                            <LocationPicker
                              customerId={customerId}
                              selectedLocationId={
                                salesOrderDraft.billToLocationId
                              }
                              onLocationSelect={handleBillToLocationSelect}
                              error={validationErrors.billToLocation}
                              siteUseCode="BILL_TO"
                            />
                          ) : salesOrderDraft.billToLocationId ? (
                            <LocationPill
                              locationId={salesOrderDraft.billToLocationId}
                              customerId={customerId}
                            />
                          ) : (
                            <MDTypography
                              variant="body2"
                              color="text.secondary"
                            >
                              No location assigned
                            </MDTypography>
                          )
                        ) : (
                          <MDTypography variant="body2">
                            Please select a customer
                          </MDTypography>
                        )}
                      </MDBox>
                    </>
                  ) : null}

                  {isEdiSource &&
                  hasAddressData(salesOrderDraft.buyingPartyAddress) ? (
                    <>
                      <MDBox className="label">Buying Party Location:</MDBox>
                      <MDBox sx={{ width: '100%', maxWidth: 400 }}>
                        {customerId ? (
                          isViewEditable ? (
                            <LocationPicker
                              customerId={customerId}
                              selectedLocationId={
                                salesOrderDraft.buyingPartyLocationId
                              }
                              onLocationSelect={handleBuyingPartyLocationSelect}
                              error={validationErrors.buyingPartyLocation}
                            />
                          ) : salesOrderDraft.buyingPartyLocationId ? (
                            <LocationPill
                              locationId={salesOrderDraft.buyingPartyLocationId}
                              customerId={customerId}
                            />
                          ) : (
                            <MDTypography
                              variant="body2"
                              color="text.secondary"
                            >
                              No location assigned
                            </MDTypography>
                          )
                        ) : (
                          <MDTypography variant="body2">
                            Please select a customer
                          </MDTypography>
                        )}
                      </MDBox>
                    </>
                  ) : null}

                  <MDTypography className="label">Ship Date:</MDTypography>
                  {isViewEditable ? (
                    <DatePicker
                      className={cn(
                        'w-56',
                        salesOrderDraft.shipDate &&
                          isDatePast(salesOrderDraft.shipDate, true) &&
                          'border-red-500',
                      )}
                      placeholder="Set a ship date"
                      value={parseDateOnly(salesOrderDraft.shipDate)}
                      onSelect={handleInlineShipDateUpdate}
                      disabled={{
                        before: (() => {
                          const t = new Date();
                          t.setHours(0, 0, 0, 0);
                          return t;
                        })(),
                        after: parseDateOnly(salesOrderDraft.deliveryDate),
                      }}
                    />
                  ) : salesOrderDraft.shipDate ? (
                    <MDTypography variant="body2">
                      {parseDateOnly(
                        salesOrderDraft.shipDate,
                      )?.toLocaleDateString()}
                    </MDTypography>
                  ) : (
                    <MDBox>
                      <MDTypography variant="body2">-</MDTypography>
                    </MDBox>
                  )}

                  <MDTypography className="label">Delivery Date:</MDTypography>
                  {isViewEditable ? (
                    <DatePicker
                      className={cn(
                        'w-56',
                        salesOrderDraft.deliveryDate &&
                          isDatePast(salesOrderDraft.deliveryDate, true) &&
                          'border-red-500',
                      )}
                      placeholder="Set a delivery date"
                      value={parseDateOnly(salesOrderDraft.deliveryDate)}
                      onSelect={handleInlineDeliveryDateUpdate}
                      disabled={{
                        before: (() => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const ship = parseDateOnly(salesOrderDraft.shipDate);
                          return ship && ship > today ? ship : today;
                        })(),
                      }}
                    />
                  ) : salesOrderDraft.deliveryDate ? (
                    <MDTypography variant="body2">
                      {parseDateOnly(
                        salesOrderDraft.deliveryDate,
                      )?.toLocaleDateString()}
                    </MDTypography>
                  ) : (
                    <MDBox>
                      <MDTypography variant="body2">-</MDTypography>
                    </MDBox>
                  )}

                  <MDTypography className="label">Total:</MDTypography>
                  <MDBox display="flex" flexDirection="column">
                    <MDTypography variant="body2">
                      {salesOrderDraft.totalAmount &&
                      !isNaN(parseFloat(salesOrderDraft.totalAmount))
                        ? `$${parseFloat(
                            salesOrderDraft.totalAmount,
                          ).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}`
                        : '-'}
                    </MDTypography>
                    {salesOrderDraft.extractedTotalAmount &&
                      salesOrderDraft.totalAmount &&
                      parseFloat(
                        String(salesOrderDraft.extractedTotalAmount),
                      ) !== parseFloat(String(salesOrderDraft.totalAmount)) && (
                        <MDTypography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontStyle: 'italic', mt: 0.25 }}
                        >
                          Note: We initially extracted a total amount of $
                          {parseFloat(
                            String(salesOrderDraft.extractedTotalAmount),
                          ).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </MDTypography>
                      )}
                  </MDBox>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>

          {/* Line items */}
          <MDBox sx={{ flex: 1, minHeight: 0 }} mb={showChargeModal ? 2 : 0}>
            {validationErrors.inactiveProducts && (
              <MDBox mb={2}>
                <MDTypography variant="caption" color="error">
                  {validationErrors.inactiveProducts}
                </MDTypography>
              </MDBox>
            )}
            <EnhancedLineItemsTable
              lineItems={displayLineItems}
              editable={isViewEditable}
              isLoading={isLoading}
              onDeleteLineItem={handleDeleteLineItem}
              onUpdateLineItem={handleUpdateLineItem}
              onToggleRejectLineItem={handleToggleRejectLineItem}
              pendingUpdates={pendingUpdates}
              onSelectLineItem={
                isAssociateProductV2Enabled
                  ? undefined
                  : (index) => {
                      setSelectedLineItemIndex(index);
                      setSelectedLineItemSnapshot({
                        ...lineItems[index],
                        ...optimisticUpdates[index],
                      });
                    }
              }
              customerId={customerId}
              customerName={customerName}
              onAssociateVariantSelected={handleAssociateVariantSelected}
              isEdiSource={isEdiSource}
              isEmailSource={isEmailSource}
              stagedPurchaseOrderId={salesOrderDraft?.id}
              inventoryResults={inventoryData?.results ?? {}}
              isInventoryLoading={isInventoryLoading}
              discountResults={discountData?.results ?? {}}
              isDiscountLoading={isDiscountLoading}
              showInventoryColumn={showInventoryColumn}
            />
          </MDBox>

          {/* Order-level charges */}
          {!showChargeModal ? null : (
            <MDBox mb={2}>
              <ChargesSection
                charges={salesOrderDraft.charges || []}
                onAddCharge={handleAddOrderCharge}
                onUpdateCharge={handleUpdateOrderCharge}
                onDeleteCharge={handleDeleteOrderCharge}
                editable={isViewEditable}
                isLoading={
                  addOrderCharge.isLoading ||
                  updateOrderCharge.isLoading ||
                  deleteOrderCharge.isLoading
                }
                subtotal={displayLineItems.reduce((sum, item) => {
                  if (item.quantity && item.unitPrice) {
                    return sum + item.quantity * item.unitPrice;
                  }
                  return sum + (item.amount || 0);
                }, 0)}
                title="Order Charges"
                emptyStateText="No order-level charges added"
              />
            </MDBox>
          )}
        </Grid>

        {/* Right side - Activity Graph / Source Document Viewer */}
        {isRightPanelVisible && (
          <Grid
            item
            xs={12}
            md={isActivityLogEnabled ? 5 : 6}
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            {/* AI Suggestion panel */}
            {(generateSuggestion.isPending || isAiSuggestionGenerating) && (
              <AiSuggestionLoading className="mb-2 shrink-0" />
            )}
            {isAiSuggestionFailed && aiSuggestion && (
              <AiSuggestionError
                errorMessage={aiSuggestion.errorMessage}
                onRetry={handleRefreshSuggestion}
                isRetrying={generateSuggestion.isPending}
                className="mb-2 shrink-0"
              />
            )}
            {canGenerateSuggestion &&
              !generateSuggestion.isPending &&
              !isAiSuggestionGenerating && (
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-end"
                  mb={1}
                  className="shrink-0"
                >
                  <MDButton
                    variant="text"
                    color="info"
                    size="small"
                    onClick={handleRefreshSuggestion}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 400,
                      fontSize: '0.75rem',
                      opacity: 0.8,
                      '&:hover': { opacity: 1 },
                    }}
                  >
                    <Icon sx={{ fontSize: 14, mr: 0.5 }}>auto_awesome</Icon>
                    Get AI help
                  </MDButton>
                </MDBox>
              )}
            {aiSuggestion &&
              aiSuggestion.status === 'pending' &&
              aiSuggestion.suggestedActions &&
              aiSuggestion.suggestedActions.length > 0 && (
                <SuggestedActionsPanel
                  actions={aiSuggestion.suggestedActions}
                  onApplyAction={handleApplySuggestedAction}
                  onDismissAction={handleDismissSuggestedAction}
                  onReviewDraft={handleReviewSuggestedDraft}
                  onDismissAll={handleDismissSuggestion}
                  onRerun={handleRefreshSuggestion}
                  applyingActionIndex={applyingActionIndex}
                  className="mb-2 shrink-0"
                />
              )}
            {/* Fallback to old banner if no suggestedActions array */}
            {aiSuggestion &&
              aiSuggestion.status === 'pending' &&
              (!aiSuggestion.suggestedActions ||
                aiSuggestion.suggestedActions.length === 0) && (
                <AiSuggestionBanner
                  suggestion={{
                    id: aiSuggestion.id,
                    // When all suggested actions have been applied (suggestedActions is empty),
                    // always treat as approve_order so the green banner is shown.
                    // The email draft action was already stripped by the backend if one
                    // was previously sent, so we don't need to check emailDraftId here.
                    suggestionType:
                      aiSuggestion.suggestedActions?.length === 0
                        ? 'approve_order'
                        : (aiSuggestion.suggestionType as
                            | 'approve_order'
                            | 'send_email_draft'),
                    status: aiSuggestion.status as
                      | 'pending'
                      | 'accepted'
                      | 'rejected'
                      | 'invalidated',
                    summary: aiSuggestion.summary ?? null,
                    actionReason: aiSuggestion.actionReason ?? null,
                    emailDraftId: aiSuggestion.emailDraftId,
                  }}
                  onReviewDraft={handleReviewEmailDraft}
                  onAccept={() => {
                    // When suggestedActions is empty, all actions are applied — approve
                    if (
                      aiSuggestion.suggestedActions?.length === 0 ||
                      aiSuggestion.suggestionType === 'approve_order'
                    ) {
                      onApproveOrder?.();
                    } else {
                      handleAcceptSuggestion();
                    }
                  }}
                  onReject={handleDismissSuggestion}
                  onRefresh={handleRefreshSuggestion}
                  isLoading={
                    acceptSuggestion.isPending || dismissSuggestion.isPending
                  }
                  isRefreshing={generateSuggestion.isPending}
                  className="mb-2 shrink-0"
                />
              )}
            {aiSuggestion && aiSuggestion.status === 'dismissed' && (
              <MDBox
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                px={2}
                py={1}
                mb={2}
                sx={{
                  backgroundColor: 'grey.100',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'grey.300',
                }}
                className="shrink-0"
              >
                <MDTypography variant="caption" color="text.secondary">
                  <Icon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }}>
                    visibility_off
                  </Icon>
                  AI suggestion was dismissed
                </MDTypography>
                <MDButton
                  variant="text"
                  color="info"
                  size="small"
                  onClick={handleRefreshSuggestion}
                  disabled={generateSuggestion.isPending}
                >
                  {generateSuggestion.isPending
                    ? 'Generating...'
                    : 'Regenerate'}
                </MDButton>
              </MDBox>
            )}
            {isActivityLogEnabled ? (
              <ActivityGraph
                entityType={SystemEntity.AR_STAGED_SALES_ORDER}
                entityId={salesOrderDraftId}
                conversationId={salesOrderDraft?.sourceEmail?.conversationId}
                className="h-full min-h-0"
              />
            ) : (
              <div className="flex-1 min-h-0 overflow-auto">
                <SourceDocumentViewer
                  sourceFlags={sourceFlags}
                  emailData={emailData}
                  conversationId={salesOrderDraft?.sourceEmail?.conversationId}
                  ordefulMessage={ediData.ordefulMessage}
                  document={documentData.document}
                />
              </div>
            )}
          </Grid>
        )}
      </Grid>

      {renderAssociateModal()}

      {showDeleteLineItemModal && deletingLineItem && (
        <DeleteLineItemModal
          open={showDeleteLineItemModal}
          onClose={() => {
            setShowDeleteLineItemModal(false);
            setDeletingLineItem(undefined);
            setDeletingLineItemIndex(undefined);
          }}
          onConfirm={handleConfirmDelete}
          lineItem={deletingLineItem}
          isLoading={deleteLineItem.isLoading}
          error={deleteLineItem.error || undefined}
        />
      )}

      {/* Re-extract Sales Order Draft Dialog */}
      {showReextractDialog &&
        emailData.inboundEmailId &&
        salesOrderDraft?.id && (
          <ReextractSalesOrderDraftDialog
            inboundEmailId={emailData.inboundEmailId}
            classificationId={
              salesOrderDraft?.sourceEmail?.classification?.classificationResult
            }
            open={showReextractDialog}
            onOpenChange={setShowReextractDialog}
            metadata={{
              customerEmailDomain,
            }}
            entityId={salesOrderDraft.id}
            entityType={SystemEntity.AR_STAGED_SALES_ORDER}
            onSuccess={() => {
              refetchSalesOrderDraft();
              onUpdate?.();
              showToast({
                title: 'Re-extraction Complete',
                message:
                  'Sales order draft has been re-extracted successfully.',
                color: 'success',
                icon: 'check_circle',
              });
            }}
          />
        )}

      <SyncErrorDialog
        syncErrorState={{
          isOpen: syncErrorDialogOpen,
          message: syncErrorMessage,
          erpSystemName: erpSystemName,
        }}
        onClose={() => {
          setSyncErrorDialogOpen(false);
          setSyncErrorMessage(null);
          setErpSystemName(null);
        }}
      />

      {/* Email Draft Editor for AI suggestions */}
      {emailDraft && (
        <EmailDraftEditor
          draft={{
            id: emailDraft.id,
            to: emailDraft.to,
            cc: emailDraft.cc,
            subject: emailDraft.subject,
            content: emailDraft.content,
            htmlContent: emailDraft.htmlContent,
            isAiGenerated: emailDraft.isAiGenerated,
            status: emailDraft.status,
            sendingMailbox: emailDraft.sendingMailbox,
          }}
          open={isEmailDraftEditorOpen}
          onOpenChange={setIsEmailDraftEditorOpen}
          onSave={handleSaveEmailDraft}
          onSend={handleSendEmailDraft}
          onDiscard={handleDiscardEmailDraft}
          isSaving={updateEmailDraft.isPending}
          isSending={sendEmailDraft.isPending}
        />
      )}
    </MDBox>
  );
}
